/**
 * Extraction mécanique de client.ts (WO-V3-02 / US-019).
 *
 * Cœur HTTP partagé par tous les modules `api/*Api.ts` : normalisation des erreurs,
 * pagination, jeton d'authentification. Inchangé fonctionnellement — seul le
 * placement a bougé (voir client.ts, qui reste la façade publique).
 */

/**
 * Client de la passerelle DOIT.
 *
 * Toutes les requêtes passent par `request`, qui normalise les erreurs : le reste de
 * l'application ne manipule jamais de `Response` brute et ne peut donc pas oublier de
 * traiter un échec. Une erreur porte son code HTTP et son message, jamais le corps brut
 * d'une réponse susceptible de contenir des données sensibles.
 */

import { getToken, logout, noterMotifDeconnexion, rafraichir } from '../auth/session'
import { signalerArretConstate } from '../lib/arretIA'
import { langueCourante, traduire } from '../i18n/language'

export const BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: string,
    /** Valeur Retry-After HTTP, si le serveur l'a fournie et qu'elle est exploitable. */
    readonly retryAfterSeconds?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Une page de résultats : les éléments, et le volume TOTAL avant découpe. */
export interface Page<T> {
  items: T[]
  /** Nombre total d'éléments côté serveur, indépendant de la taille de page. */
  total: number
}

/**
 * Lit une collection paginée.
 *
 * Toute route de liste accepte `limit` et `offset` depuis le 2026-08-15, et annonce le
 * volume total dans l'en-tête `X-Total-Count` (`config/PaginationAdvice.java`). Jusque-là,
 * UNE seule route sur 163 acceptait un paramètre de pagination : les autres rendaient la
 * collection entière, ce qui interdisait toute montée en volume.
 *
 * Le total vient de l'en-tête et NON de `items.length` : c'est toute la différence entre
 * « voici 20 éléments » et « voici 20 éléments sur 4 312 ». Sans lui, aucune interface ne
 * peut afficher un nombre de pages ni proposer d'aller plus loin.
 *
 * Sans `limit` ni `offset`, le serveur ne tronque rien — appeler cette fonction sans page
 * demandée reste donc équivalent à l'ancien comportement.
 */
export async function requestPage<T>(
  path: string,
  page?: { limit?: number; offset?: number },
  init?: RequestInit,
): Promise<Page<T>> {
  const params = new URLSearchParams()
  if (page?.limit != null) params.set('limit', String(page.limit))
  if (page?.offset != null) params.set('offset', String(page.offset))
  const separateur = path.includes('?') ? '&' : '?'
  const chemin = params.toString() ? `${path}${separateur}${params}` : path

  const { corps, reponse } = await requestAvecReponse<T[]>(chemin, init)
  const entete = reponse.headers.get('X-Total-Count')
  // En-tête absent (proxy qui le filtre, endpoint hors norme) : on retombe sur la taille
  // reçue plutôt que sur zéro. Mieux vaut un total sous-estimé qu'un « aucun résultat »
  // affiché au-dessus d'une liste pleine.
  return { items: corps, total: entete != null ? Number(entete) : corps.length }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { corps } = await requestAvecReponse<T>(path, init)
  return corps
}

/**
 * Même appel que `request`, mais rend AUSSI la réponse HTTP.
 *
 * Extrait de `request` plutôt que dupliqué : la gestion des erreurs — panne réseau,
 * messages métier actionnables des 400/403, 204 sans corps — n'existe qu'à un seul
 * endroit. Deux copies auraient divergé, comme le DDL de core-ia avait divergé du schéma.
 * Seuls les appelants ayant besoin d'un en-tête (pagination et son `X-Total-Count`)
 * passent par ici.
 */
export async function requestAvecReponse<T>(
  path: string, init?: RequestInit,
): Promise<{ corps: T; reponse: Response }> {
  let response: Response
  try {
    const token = getToken()
    const multipart = typeof FormData !== 'undefined' && init?.body instanceof FormData
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(multipart ? {} : { 'Content-Type': 'application/json' }),
        // Le jeton est ajouté ici et nulle part ailleurs : aucun appelant ne peut
        // oublier de l'inclure, et aucun ne peut le remplacer par erreur.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {}),
      },
    })
  } catch (cause) {
    // Panne réseau : distinguée d'une erreur applicative pour que l'UI propose
    // « réessayer » plutôt qu'afficher un message d'erreur métier trompeur.
    throw new ApiError(0, 'Service injoignable', String(cause))
  }

  if (!response.ok) {
    // Une session invalide est un état global, pas une erreur de panneau. Effacer
    // l'identité ici déclenche le listener App et ramène une seule fois à la connexion,
    // au lieu de laisser chaque écran afficher son propre message bloqué.
    const detail = await response.text().catch(() => undefined)
    if (response.status === 401) {
      // Lot USR : deux 401 NOMMÉS de la passerelle. TOKEN_ROLE_STALE — les droits ont changé
      // depuis l'émission du jeton : un rafraîchissement suffit (nouveau jeton, nouveaux rôles ;
      // `rafraichir` déconnecte s'il échoue). SESSION_MAX_DURATION_EXCEEDED — la politique de
      // l'organisation impose de se reconnecter : le motif est noté pour l'écran de connexion.
      const code = codeDuCorps(detail)
      if (code === 'TOKEN_ROLE_STALE') {
        void rafraichir()
        throw new ApiError(401, messageDeCode(code) ?? 'Vos droits ont changé : réessayez.', detail)
      }
      if (code === 'SESSION_MAX_DURATION_EXCEEDED') noterMotifDeconnexion(code)
      logout()
    }
    // U6b vague 1 (§ 4.10.1) : un refus pour arrêt d'urgence lève le bandeau global, même pour
    // un membre qui ne peut pas lire l'état des bascules (réservé à l'administration).
    if (response.status === 503 && detail?.includes('AI_KILL_SWITCH_ACTIVE')) signalerArretConstate()
    let message = httpMessage(response.status)
    // Une erreur de validation (400/422) ou un refus MÉTIER (403/409, ex. SoD-02) porte souvent
    // un message ACTIONNABLE de la passerelle (« SoD-02 : l'auteur… », « scope_files is
    // required… »). On le préfère au libellé générique — dire quoi faire, pas seulement
    // « refusé ». On écarte les libellés génériques du serveur (« Access denied » d'un
    // simple refus de rôle garde notre message français orienté utilisateur).
    if ([400, 403, 409, 422].includes(response.status) && detail) {
      try {
        const corps = JSON.parse(detail) as { message?: string; code?: string }
        const generiques = ['Validation failed', 'Access denied']
        if (corps?.message && corps.message.trim() && !generiques.includes(corps.message)) {
          message = corps.message
        }
        // FND-V050-171 : un refus dont le code a sa phrase au catalogue (`erreurs.code.<CODE>`) se
        // dit dans la langue de l'interface — jamais le message anglais brut de la passerelle.
        const traduit = messageDeCode(corps?.code)
        if (traduit) message = traduit
      } catch {
        // corps non-JSON : on garde le message générique orienté utilisateur
      }
    }
    const retryAfter = Number(response.headers.get('Retry-After'))
    const retryAfterSeconds = Number.isSafeInteger(retryAfter) && retryAfter > 0
      ? retryAfter
      : undefined
    throw new ApiError(response.status, message, detail, retryAfterSeconds)
  }
  if (response.status === 204) return { corps: undefined as T, reponse: response }
  return { corps: (await response.json()) as T, reponse: response }
}

/** Code nommé d'un corps d'erreur JSON de la passerelle, ou `null`. */
function codeDuCorps(detail: string | undefined): string | null {
  if (!detail) return null
  try {
    const corps = JSON.parse(detail) as { code?: unknown }
    return typeof corps.code === 'string' ? corps.code : null
  } catch {
    return null
  }
}

/** Messages orientés utilisateur : ils disent quoi faire, pas ce qui a cassé. */
/** Phrase du catalogue pour un code de refus de la passerelle, ou `null` s'il n'en a pas. */
export function messageDeCode(code: string | null | undefined): string | null {
  if (!code || !/^[A-Z][A-Z0-9_]*$/.test(code)) return null
  const cle = `erreurs.code.${code}`
  const texte = traduire(langueCourante(), cle)
  return texte === cle ? null : texte
}

export function httpMessage(status: number): string {
  if (status === 401) return 'Session expirée — reconnectez-vous'
  if (status === 403) return "Vous n'avez pas les droits nécessaires"
  if (status === 404) return 'Ressource introuvable'
  if (status === 409) return 'Conflit : la ressource a changé entre-temps'
  if (status === 429) return 'Quota dépassé — réessayez dans un instant'
  if (status >= 500) return 'Erreur du service — réessayez'
  return 'Requête refusée'
}
